import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  listGlobalSections, type GlobalSection, type SectionType,
} from '../../lib/cmsAdmin';
import { ApiError } from '../../lib/apiClient';

const TYPE_LABEL: Record<SectionType, string> = {
  footer: 'Footer',
  announcement: 'Announcement',
  sponsor_strip: 'Sponsor strip',
};

/**
 * Choose a global section for a GlobalSection block.
 *
 * ── IT OFFERS ONLY WHAT WOULD PUBLISH ────────────────────────────────────────────────
 *
 * §18. Filtered to sections this owner may legally reference and that have actually been
 * published. A picker that offered an unpublished section, or another tenant's, would be
 * offering a choice the publish gate refuses — which is a dead end dressed up as an
 * option. The server re-checks all of it regardless; this stops somebody finding out at
 * Publish.
 *
 * A platform template renders for every organization, so it may only reference platform
 * sections. An organization's template may reference its own and the platform's.
 */
export function GlobalSectionPicker({
  value, organizationId, scope, onChange, onClose,
}: {
  value: string | null;
  organizationId: string | null;
  scope: 'platform' | 'organization';
  onChange: (sectionId: string | null) => void;
  onClose: () => void;
}) {
  const [sections, setSections] = useState<GlobalSection[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    listGlobalSections({ organizationId })
      .then((rows) => { if (!cancelled) setSections(rows); })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Global sections could not be loaded.');
        }
      });
    return () => { cancelled = true; };
  }, [organizationId]);

  const { usable, unpublished } = useMemo(() => {
    const term = search.trim().toLowerCase();
    const inScope = (sections ?? []).filter((s) => {
      if (scope === 'platform' && s.scope !== 'platform') return false;
      if (!term) return true;
      return s.name.toLowerCase().includes(term);
    });
    return {
      usable: inScope.filter((s) => s.publishedVersion != null),
      // Shown separately rather than hidden: "my footer is not in the list" is otherwise
      // an unanswerable question, and the answer is usually "publish it".
      unpublished: inScope.filter((s) => s.publishedVersion == null),
    };
  }, [sections, scope, search]);

  return (
    <div className="cms-modal" role="dialog" aria-modal="true" aria-label="Choose a global section">
      <div className="cms-modal__panel cms-modal__panel--wide">
        <header className="cms-modal__head">
          <h2>Choose a global section</h2>
          <button type="button" className="button button--ghost adm-btn-sm" onClick={onClose}>
            Close
          </button>
        </header>

        <p className="alert alert--warning">
          This is a <strong>live reference</strong>. Whatever you choose is rendered from its
          own current published version, so editing it later changes every page that
          references it.
        </p>

        <label className="field">
          <span className="field__label">Search</span>
          <input className="field__input" value={search} placeholder="Section name"
            onChange={(e) => setSearch(e.target.value)} />
        </label>

        {error && <p className="alert alert--error" role="alert">{error}</p>}
        {!sections && !error && <p className="adm-muted">Loading…</p>}

        {sections && usable.length === 0 && (
          <p className="adm-muted">
            {scope === 'platform'
              ? 'No published platform sections yet. A platform template renders for every '
                + 'organization, so it can only use platform-owned sections.'
              : 'No published sections yet. Create one in Content & design → Global sections.'}
          </p>
        )}

        <ul className="cms-picker cms-picker--sections">
          {usable.map((section) => (
            <li key={section.id}>
              <button
                type="button"
                className={`cms-picker__item${value === section.id ? ' is-selected' : ''}`}
                onClick={() => { onChange(section.id); onClose(); }}
              >
                <span className="cms-picker__name">{section.name}</span>
                <span className="cms-picker__meta">
                  {TYPE_LABEL[section.sectionType]}
                  {section.scope === 'platform' ? ' · Platform' : ''}
                  {` · live v${section.publishedVersion}`}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {unpublished.length > 0 && (
          <section className="cms-picker__unavailable">
            <h3>Not available yet</h3>
            <p className="adm-hint">
              These have never been published, so referencing one would stop this page
              being published.
            </p>
            <ul>
              {unpublished.map((section) => (
                <li key={section.id}>
                  <Link to={`/admin/content/global-sections/${section.id}`}>{section.name}</Link>
                  {' — '}{TYPE_LABEL[section.sectionType]}
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="cms-modal__foot">
          <button type="button" className="button button--ghost"
            onClick={() => { onChange(null); onClose(); }}>
            Clear this reference
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * The read-only face of a section reference: what is selected, and how to reach it.
 *
 * §65: the source is NOT editable from here. A footer edited inside a template editor
 * would look like it belonged to that template, and the person editing it would have no
 * idea they were changing forty other pages. Opening it in its own editor makes the
 * reference obvious.
 */
export function SectionSlot({
  sectionId, onOpen,
}: { sectionId: string | null; onOpen: () => void }) {
  const [section, setSection] = useState<GlobalSection | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!sectionId) { setSection(null); setMissing(false); return undefined; }
    let cancelled = false;
    listGlobalSections()
      .then((rows) => {
        if (cancelled) return;
        const found = rows.find((s) => s.id === sectionId) ?? null;
        setSection(found);
        setMissing(!found);
      })
      .catch(() => { if (!cancelled) setMissing(true); });
    return () => { cancelled = true; };
  }, [sectionId]);

  return (
    <div className="cms-slot cms-slot--section">
      <div className="cms-slot__text">
        {!sectionId && <span className="adm-muted">No section chosen. This block renders nothing.</span>}
        {sectionId && section && (
          <>
            <span className="cms-slot__title">
              {TYPE_LABEL[section.sectionType]} — {section.name}
            </span>
            {section.publishedVersion
              ? <span className="adm-muted">Live v{section.publishedVersion}</span>
              : (
                <span className="cms-slot__missing">
                  Never published. This page cannot be published while it references it.
                </span>
              )}
          </>
        )}
        {sectionId && missing && (
          <span className="cms-slot__missing">
            This block references a section that no longer exists or is not visible to you.
          </span>
        )}
      </div>
      <div className="cms-slot__actions">
        <button type="button" className="button button--ghost adm-btn-sm" onClick={onOpen}>
          {sectionId ? 'Change' : 'Choose'}
        </button>
        {sectionId && section && (
          <Link className="button button--ghost adm-btn-sm"
            to={`/admin/content/global-sections/${section.id}`}>
            Open
          </Link>
        )}
      </div>
    </div>
  );
}
